/* Card interactions — colour swap, size→cart, mobile reveal.
   Deliberately separate from product-card-info.js, which owns variant-option
   changes for four sections and should not grow more responsibilities. */
if (!customElements.get('card-interactions')) {
  /* Auto-reveal on mobile (owner request, 2026-08-02): a card's sizes fade in
     when it scrolls into view and back out when it leaves, with no tap on the
     cart button.

     Matches the media query the mobile-reveal CSS block uses, so the JS and
     the stylesheet agree on what "mobile" means. `(hover: none)` is part of
     it for the same reason it is there: a hoverless tablet at any width has
     no :hover reveal to fall back on.

     ONE observer shared by every card, not one per instance. A collection
     page renders 24 cards after scrolling, and 24 IntersectionObservers each
     watching a single element is pure overhead for identical options. */
  const REVEAL_MEDIA = '(max-width: 749px), (hover: none)';
  let autoRevealObserver = null;

  const getAutoRevealObserver = () => {
    if (autoRevealObserver) return autoRevealObserver;
    autoRevealObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          /* The observed node is the card's BOX, not the custom element -
             see syncAutoReveal for why - so resolve back up to the host. */
          entry.target.closest('card-interactions')?.setAutoReveal?.(entry.isIntersecting);
        });
      },
      /* Opens when the card reaches roughly the MIDDLE of the screen, not
         merely when it enters view (owner request, 2026-08-02).

         `rootMargin` shrinks the observation root by 40% top and bottom,
         leaving a band across the middle 20% of the viewport. A card
         "intersects" only while it overlaps that band, so the flyout follows
         whatever the shopper has scrolled to the centre and closes again as
         it passes. Widen the band by reducing the two percentages (-35%
         gives the middle 30%); narrow it by raising them.

         threshold MUST be 0 here, and that is not a loose default. The ratio
         is measured against the TARGET's own area, and with a mobile card
         about 285px tall against a ~140px band, the greatest ratio
         achievable is roughly 0.49 - so the previous 0.55 would have made
         the callback mathematically incapable of ever reporting
         `isIntersecting`, exactly the silent no-op this feature already hit
         once. 0 means "any overlap with the band". */
      { rootMargin: '-40% 0px -40% 0px', threshold: 0 }
    );
    return autoRevealObserver;
  };

  customElements.define(
    'card-interactions',
    class CardInteractions extends HTMLElement {
      connectedCallback() {
        this.addEventListener('click', this.onClick);
        this.syncRevealVisibility();

        /* Re-evaluated on media change, not just at connect: a tablet rotated
           from portrait to landscape can cross the 749px boundary without any
           card being re-created, and a card left open by the observer would
           otherwise stay open into a breakpoint where the CSS expects hover
           to own the flyout. */
        this.revealMedia = window.matchMedia(REVEAL_MEDIA);
        this.onRevealMediaChange = () => this.syncAutoReveal();
        this.revealMedia.addEventListener('change', this.onRevealMediaChange);
        this.syncAutoReveal();

        /* Clearing the size-chip "adding" state needs a completion signal,
           and product-form.js does not give the caller one - it swallows the
           whole submit internally. What it DOES do is publish
           PUB_SUB_EVENTS.cartUpdate on success (product-form.js:71) and
           .cartError on failure (product-form.js:49). Both are global
           (constants.js/pubsub.js, theme.liquid:61-62, deferred in the head
           so they always execute before this file), so subscribe rather than
           poll or observe the hidden submit button's `loading` class.

           BOTH events matter. cartError is what fires on this store's known
           422 (Liquid reports a variant available, the Cart API rejects it -
           COLLECTION-GRID-FINDINGS.md), and subscribing to success alone
           would leave the chip lit for the full timeout on exactly the
           products most likely to fail.

           Deliberately not filtered by source: any cart resolution means the
           request this card started is over, and a card whose chip is stuck
           lit is worse than one cleared slightly early by an unrelated
           update. */
        this.cartSubscriptions =
          typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined'
            ? [
                subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.clearAddingState()),
                subscribe(PUB_SUB_EVENTS.cartError, () => this.clearAddingState()),
              ]
            : [];
      }

      /* card-product-var.liquid only wraps the reveal button in `{% unless
         product.has_only_default_variant %}`, which catches a product with
         no size option at all but cannot cheaply predict Task 7's
         hide_unavailable filter stripping every remaining VALUE out of a
         size option that does exist (a product whose only sizes are
         unavailable in the current market, for example) - Liquid does not
         know the post-filter row count at render time. Hide the reveal
         control whenever no size row actually rendered, so it can never
         become a dead tap target with no flyout behind it. Runs again after
         a colour swap replaces this element, since connectedCallback
         re-fires on the replacement. An inline style, not a class/attribute
         toggle, because this file's own CSS sets `display: flex` on this
         element with no !important - only an inline style is guaranteed to
         win over that regardless of selector specificity. */
      syncRevealVisibility() {
        const reveal = this.querySelector('[data-card-reveal]');
        if (!reveal) return;
        const hasSizes = !!this.querySelector('.card__information-sizes--size label');
        reveal.style.display = hasSizes ? '' : 'none';
      }

      /* Start or stop watching this card, per the current breakpoint. Called
         at connect and again whenever the media query flips.

         Observes `.card--var`, NOT `this`. This element is `display: contents`
         (component-card-fix.css's "Interaction wrapper" rule, so the wrapper
         cannot become a containing block for the absolutely positioned flyout
         and heart) - and an element with `display: contents` generates no box
         at all. IntersectionObserver measures a box, so observing this host
         silently never fires: its client rect is 0x0 and can never intersect
         the viewport. That is exactly how this shipped broken the first time.
         `.card--var` is the real card box inside it. */
      syncAutoReveal() {
        const observer = getAutoRevealObserver();
        const target = this.querySelector('.card--var') || this.firstElementChild;

        if (this.autoRevealTarget && this.autoRevealTarget !== target) {
          observer.unobserve(this.autoRevealTarget);
          this.autoRevealTarget = null;
        }
        if (!target) return;

        if (this.revealMedia?.matches) {
          this.autoRevealTarget = target;
          observer.observe(target);
        } else {
          observer.unobserve(target);
          this.autoRevealTarget = null;
          this.closeReveal(this.querySelector('[data-card-reveal]'));
        }
      }

      /* Driven by the shared observer. Deliberately NOT routed through
         onRevealClick: that registers a document-level `closeOnOutside`
         listener so a tap anywhere else dismisses a manually-opened panel.
         With auto-reveal every visible card is open at once, so reusing it
         would attach a document click listener per card and a single tap
         would collapse the whole screen's worth of flyouts. Auto-opened
         panels are dismissed by scrolling away, which is what opened them.

         Still calls closeReveal() on the way out, so a card that was opened
         BY TAP and then scrolled off does get its listener cleaned up. */
      setAutoReveal(visible) {
        const button = this.querySelector('[data-card-reveal]');

        if (!visible) {
          this.closeReveal(button);
          return;
        }

        /* Same guard syncRevealVisibility() applies to the button: a product
           whose sizes were all stripped by the hide_unavailable filter has an
           empty flyout, and opening it would paint an empty box over the
           image. */
        if (!this.querySelector('.card__information-sizes--size label')) return;

        this.classList.add('card-interactions--open');
        button?.setAttribute('aria-expanded', 'true');
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onClick);
        if (this.closeOnOutside) {
          document.removeEventListener('click', this.closeOnOutside);
          this.closeOnOutside = null;
        }
        /* A colour swap replaces this element, so both of these would
           otherwise accumulate against detached nodes on every swap. The
           observer is shared and long-lived, which makes unobserving it here
           necessary rather than optional. */
        if (this.autoRevealTarget) {
          autoRevealObserver?.unobserve(this.autoRevealTarget);
          this.autoRevealTarget = null;
        }
        if (this.revealMedia && this.onRevealMediaChange) {
          this.revealMedia.removeEventListener('change', this.onRevealMediaChange);
          this.onRevealMediaChange = null;
        }
        if (this.cartSubscriptions) {
          this.cartSubscriptions.forEach((unsubscribe) => unsubscribe());
          this.cartSubscriptions = [];
        }
        /* A colour swap replaces this element mid-flight; without this the
           timer would fire against a detached node. Harmless in itself, but
           it is the same class of leak the swap's own guards exist for. */
        if (this.addingTimeout) {
          clearTimeout(this.addingTimeout);
          this.addingTimeout = null;
        }
      }

      onClick = (event) => {
        const swatch = event.target.closest('.product-related-item a');
        if (swatch && this.contains(swatch)) {
          this.onSwatchClick(event, swatch);
          return;
        }
        const reveal = event.target.closest('[data-card-reveal]');
        if (reveal && this.contains(reveal)) {
          this.onRevealClick(event, reveal);
          return;
        }
        const size = event.target.closest('.card__information-sizes--size label');
        if (size && this.contains(size)) {
          this.onSizeClick(event, size);
        }
      };

      /* --- size -> cart --------------------------------------------------- */

      onSizeClick(event, label) {
        const variantId = this.resolveVariantId(label);
        if (!variantId) return; // let the label behave normally

        // Suppresses the label's native forwarding to its radio input, so
        // the checked radio does NOT move to the clicked size. That is
        // deliberate: letting it through would fire
        // optionValueSelectionChange, which makes product-card-info.js
        // re-fetch the whole section on every size click. Do not remove
        // this to "fix" the visual selection state.
        event.preventDefault();

        const productForm = this.querySelector('.product-card__buy-button product-form');
        const form = productForm?.querySelector('form');
        const idInput = form?.querySelector('input[name="id"]');
        if (!form || !idInput) return;

        /* Ignore repeat clicks while a request is in flight. Without this,
           impatient taps on a slow connection each fire their own
           requestSubmit and each add another unit to the cart - the shopper
           sees nothing happening and taps again. The visual state below and
           this guard are the same fix. */
        if (this.addingLabel) return;

        idInput.disabled = false;
        idInput.value = variantId;

        /* The theme DOES render a spinner for this submit, but it lives
           inside the card's buy button (card-buy-buttons.liquid), which this
           card never shows - the size flyout replaced it. product-form.js:27
           dutifully toggles `loading` on a control nobody can see, so from
           the shopper's side the tap produced no feedback at all until the
           cart drawer opened. Put the state on the chip they actually
           touched. */
        this.setAddingState(label);

        // Goes through the theme's own product-form, which publishes
        // PUB_SUB_EVENTS.cartUpdate and opens the cart drawer.
        form.requestSubmit();
      }

      setAddingState(label) {
        this.addingLabel = label;
        label.classList.add('card__size--adding');
        this.classList.add('card-interactions--adding');

        /* Fallback clear. cartUpdate never publishes when the add FAILS -
           product-form.js handles its own errors and returns - and this store
           has a live 422 defect (COLLECTION-GRID-FINDINGS.md) where Liquid
           reports a variant available that the Cart API then rejects. Without
           a timeout the chip would stay lit forever on exactly the products
           most likely to be tapped twice. 8s is long enough not to race a
           slow-but-succeeding request. */
        this.addingTimeout = setTimeout(() => this.clearAddingState(), 8000);
      }

      clearAddingState() {
        if (this.addingTimeout) {
          clearTimeout(this.addingTimeout);
          this.addingTimeout = null;
        }
        if (!this.addingLabel) return;
        this.addingLabel.classList.remove('card__size--adding');
        this.classList.remove('card-interactions--adding');
        this.addingLabel = null;
      }

      resolveVariantId(label) {
        const input = label.control || this.querySelector(`#${CSS.escape(label.htmlFor)}`);
        if (input?.classList.contains('disabled')) return null;

        const direct = input?.dataset.variantIdValue;
        if (direct) return direct;

        return this.resolveFromJson(this.labelValueText(label));
      }

      /* The label always carries a visually-hidden a11y span
         ("Variant sold out or unavailable" - see
         snippets/product-variant-options-var.liquid's label_unavailable)
         in addition to the visible <p>{{ value }}</p>, on every row,
         available or not. label.textContent would include both and could
         never match a clean option value like "36" - read the <p> alone.

         `data-option-value` is preferred over the text because the text is
         no longer guaranteed to BE the option value: "One size" renders as
         "OS" (see that snippet's value_display). resolveFromJson matches
         this against the product JSON, where the value is still the full
         string, so reading "OS" would match nothing and the fallback would
         silently return null on all 41 one-size products. The textContent
         path stays for markup that predates the attribute. */
      labelValueText(label) {
        const visible = label.querySelector('p');
        const trueValue = visible?.dataset.optionValue;
        if (trueValue) return trueValue.trim();
        return (visible ? visible.textContent : label.textContent).trim();
      }

      /* Fallback when the option value carries no variant id. Colours are
         separate products in this theme, so size is the distinguishing
         option and matching on it alone is sufficient. */
      resolveFromJson(sizeValue) {
        const script = this.querySelector('[data-product-full-info]');
        if (!script) return null;
        let variants;
        try {
          variants = JSON.parse(script.textContent);
        } catch (error) {
          console.error('card-interactions: bad product JSON', error);
          return null;
        }
        const match = variants.find((v) => v.available && v.options.includes(sizeValue));
        return match ? String(match.id) : null;
      }

      /* --- mobile reveal ---------------------------------------------------
         [data-card-reveal] is a SEPARATE, dedicated button
         (snippets/card-product-var.liquid's .card__reveal-sizes) -
         deliberately NOT the real add-to-cart <product-form> submit button
         (card-buy-buttons.liquid). Fix round 1: an
         earlier version of this task reused that submit button as the
         reveal toggle instead. That was wrong - card-buy-buttons.liquid
         renders it with a real `disabled` attribute whenever the card's
         default variant is unavailable (and product-card-form.js's
         toggleSubmitButton() can set the same real `disabled` at runtime),
         and a disabled <button> dispatches no click event under any input
         method - so the reveal went permanently dead on exactly the cards
         where individual sizes could still be purchasable, only the
         default variant having failed. This button is `type="button"`, is
         never `disabled`, and lives outside any <form>, so it cannot
         inherit either failure mode. Task 8's add-to-cart path
         (onSizeClick, above) still never touches this element either way -
         it calls form.requestSubmit() directly on the <form>, bypassing
         any button/click handler entirely. */

      onRevealClick(event, button) {
        // Defensive only, not load-bearing here - a type="button" outside
        // any <form> has no default action to cancel. Kept in case this
        // element is ever nested inside a form again.
        event.preventDefault();
        const open = this.classList.toggle('card-interactions--open');
        button.setAttribute('aria-expanded', String(open));

        if (open) {
          this.closeOnOutside = (e) => {
            if (!this.contains(e.target)) this.closeReveal(button);
          };
          // Deferred (setTimeout), or this same click would close the
          // panel immediately after opening it. Reads `this.closeOnOutside`
          // LIVE at fire time rather than capturing the function reference
          // in this closure - that is load-bearing, not incidental: if the
          // reveal is toggled open/closed/open again fast enough that more
          // than one of these deferred callbacks is still pending, each
          // stale callback's `this.closeOnOutside` read AT FIRE TIME
          // resolves to whatever the most recent toggle actually set (or
          // null, if it has since closed again) - so a stale callback
          // either correctly re-adds the CURRENT listener or is a harmless
          // no-op, never a double registration or a mismatched removal.
          // Hoisting this into a local variable captured at schedule time
          // would break that self-correction: each deferred call would
          // act on its OWN stale reference instead of whatever is actually
          // current, reintroducing the exact race this line exists to
          // avoid - do not "simplify" it into one.
          setTimeout(() => document.addEventListener('click', this.closeOnOutside), 0);
        } else {
          this.closeReveal(button);
        }
      }

      closeReveal(button) {
        this.classList.remove('card-interactions--open');
        button?.setAttribute('aria-expanded', 'false');
        if (this.closeOnOutside) {
          document.removeEventListener('click', this.closeOnOutside);
          this.closeOnOutside = null;
        }
      }

      /* --- colour swap ---------------------------------------------------- */

      async onSwatchClick(event, link) {
        const href = link.getAttribute('href');
        if (!href || link.closest('.product-related-item')?.classList.contains('active')) return;

        // Re-entrancy guard. `.card-interactions--loading pointer-events:
        // none` (component-card-fix.css) blocks a second MOUSE click while
        // a swap is in flight, but keyboard activation (Enter/Space on a
        // focused <a>) fires a `click` event that pointer-events cannot
        // suppress - so this has to be a JS check against the same
        // in-flight flag, not something left to CSS. Without it, two
        // overlapping calls share the same `this` as `oldNode`: both build
        // HTMLUpdateUtility.viewTransition(this, ...), and if the second
        // fetch resolves before the first call's oldNode.remove() (500ms
        // after its own insertBefore), the second insertBefore lands its
        // new node ahead of the still-present hidden oldNode - leaving TWO
        // live <card-interactions> siblings under one .product-card-wrapper
        // permanently, since neither is ever picked up by a later cleanup.
        // Still preventDefault() here (not a bare early return like the
        // active-swatch check above) - otherwise the browser would follow
        // the OTHER colour's real href mid-swap, a spurious navigation of
        // its own.
        if (this.classList.contains('card-interactions--loading')) {
          event.preventDefault();
          return;
        }

        event.preventDefault();
        this.classList.add('card-interactions--loading');

        // Product URLs may already carry params (recommendation URLs carry
        // pr_prod_strat), and a second '?' swallows section_id.
        const separator = href.includes('?') ? '&' : '?';

        // card-product-var.liquid writes the SAME computed --ratio-percent
        // custom property inline on both `.card--var` and `.card__inner`,
        // sized for the CALLER's own `media_aspect_ratio` (portrait/adapt/
        // square - see section-main-product-redesign.css and friends).
        // sections/card-product-swap.liquid always renders with
        // `media_aspect_ratio: 'portrait'` hardcoded (see that file's own
        // top-of-file comment for why), so a swapped-in replacement would
        // otherwise silently take on a different aspect ratio than its
        // neighbours whenever the calling grid uses anything else (search
        // uses 'adapt', for one). Capture the OUTGOING card's actual
        // rendered ratio here, before the fetch, and reapply it to the
        // incoming card's corresponding elements once it's inserted (via
        // viewTransition's postProcessCallbacks, right below) - that makes
        // the replacement match its neighbours' real geometry regardless of
        // which section it lives in, independent of whatever ratio value
        // the swap section itself rendered.
        const outgoingCard = this.querySelector('.card--var');
        const outgoingCardInner = this.querySelector('.card__inner');
        const outgoingRatio = {
          card: outgoingCard ? outgoingCard.style.getPropertyValue('--ratio-percent') : '',
          cardInner: outgoingCardInner ? outgoingCardInner.style.getPropertyValue('--ratio-percent') : '',
        };
        const reapplyOutgoingRatio = (newNode) => {
          if (outgoingRatio.card) {
            newNode.querySelector('.card--var')?.style.setProperty('--ratio-percent', outgoingRatio.card);
          }
          if (outgoingRatio.cardInner) {
            newNode.querySelector('.card__inner')?.style.setProperty('--ratio-percent', outgoingRatio.cardInner);
          }
        };

        // Carry the current page's market context onto the section fetch.
        // A plain fetch() does not automatically get whatever market
        // resolution the page's own navigation used - `country` (and/or
        // `locale`) in the CURRENT url is that resolved market, and a fetch
        // that omits it can land in a different one where this exact
        // product's variants resolve as unavailable, silently emptying the
        // size flyout (hide_unavailable strips every row - confirmed with
        // curl against the local theme-dev preview: 4 size labels with
        // ?country= on the request, 0 without it even with the market
        // cookie already set). In production this is a no-op when the page
        // URL carries neither param (the common case - markets normally
        // resolve from a cookie the fetch already sends) and harmless
        // when it does (forwarding the same value the cookie already
        // implies changes nothing); it costs nothing and fixes the one
        // environment this can actually be tested against.
        const marketParams = new URLSearchParams();
        const currentParams = new URLSearchParams(window.location.search);
        ['country', 'locale'].forEach((key) => {
          const value = currentParams.get(key);
          if (value) marketParams.set(key, value);
        });
        const marketSuffix = marketParams.toString() ? `&${marketParams.toString()}` : '';

        try {
          const response = await fetch(`${href}${separator}section_id=card-product-swap${marketSuffix}`);
          if (!response.ok) throw new Error(`HTTP ${response.status}`);

          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          const next = doc.querySelector('card-interactions');
          if (!next) throw new Error('no card in section response');

          // connectedCallback re-runs on the replacement, so listeners rebind
          // with no manual teardown.
          /* The replacement card arrives as plain section markup: its Swiper
             has never been constructed, so the pagination dots vanish and the
             card stops swiping. Reported as "changes color variation, the
             slider disappears"; measured before this fix as
             `swiper-initialized` false and 0 bullets on the swapped card,
             against true and 5 bullets before the swap.

             Since 2026-08-26 this is load-bearing for a second reason: images
             past the second now carry `data-src` and are fetched by Swiper's
             lazy module (the iPhone memory fix), so without an initialised
             Swiper on the new card those images would never load at all.

             Runs as a post-insert callback rather than after viewTransition()
             so it cannot fire before the node is actually in the document. */
          const initSlider = () => this.constructor.refreshSlider();

          HTMLUpdateUtility.viewTransition(this, next, [], [reapplyOutgoingRatio, initSlider]);
          this.constructor.refreshWishlist();
        } catch (error) {
          console.error('card-interactions: colour swap failed', error);
          this.classList.remove('card-interactions--loading');

          // A stale fetch can resolve after `this` has already been removed
          // from the document entirely (e.g. a filter/sort re-rendered the
          // grid while this request was still in flight) -
          // oldNode.parentNode is null at that point, so
          // HTMLUpdateUtility.viewTransition's insertBefore throws and
          // lands here. That is a DOM-structure problem, not a network
          // failure, and this element has no meaningful "current page" left
          // to navigate away from - fail quietly instead of forcing a
          // spurious full-page navigation that has nothing to do with what
          // actually went wrong.
          if (!this.isConnected) return;

          window.location.href = href; // degrade to plain navigation
        }
      }

      /* The wishlist heart is app-injected markup; after a swap the app has to
         re-bind it. Which hook exists is app-version dependent, so try the
         known ones and stay silent if none are present.

         A third branch here used to dispatch a `wishlisthero:reload`
         CustomEvent as a last-resort guess. Verified by causal experiment,
         fix round 1: with that dispatch call physically
         removed from this file, the heart STILL comes back present and
         bound (a real button/svg inside it, not an empty shell) after a
         swap. Neither of the two named hooks below exists on this store's
         installed app version either (confirmed via
         Object.keys(window).filter(/wishlist/i) after a real swap), so
         nothing this method does was doing the rebinding — a
         `__wishlistHeroArriveScriptLoaded` global is present on this store,
         consistent with the app using arrive.js (a DOM-mutation-watching
         library) to detect its own heart markup whenever it's inserted,
         independent of any event this theme fires. Removed the dead
         dispatch rather than ship a line that does nothing; the two real
         hooks stay as a no-op-if-absent fast path for app versions where
         they do exist. */
      /* Re-runs the theme's own card-slider initialiser (pm-custom.js). That
         function only picks up `.slider--card:not(.swiper-initialized)`, so
         calling it is safe with any number of already-live sliders on the
         page - it touches just the new one. Kept as a named static beside
         refreshWishlist so the two post-swap re-bindings sit together. */
      static refreshSlider() {
        try {
          if (typeof window.initJsSlideProduct === 'function') {
            window.initJsSlideProduct();
          }
        } catch (error) {
          console.error('card-interactions: slider refresh failed', error);
        }
      }

      static refreshWishlist() {
        try {
          if (typeof window.WishlistHero?.reloadWishlistHeroButtons === 'function') {
            window.WishlistHero.reloadWishlistHeroButtons();
          } else if (typeof window.WishlistHeroReloadButtons === 'function') {
            window.WishlistHeroReloadButtons();
          }
        } catch (error) {
          console.error('card-interactions: wishlist refresh failed', error);
        }
      }
    }
  );
}
